// Norwegian year-end VAT balance evidence. Filing status belongs to step 5.
type YearClosingVatRow = { code: string; standardCode: string; name: string; balance: number; kind: 'control' | 'settlement' | 'other' }
type YearClosingVatEvidence = { year?: number; expectedBalance?: number; documentationNote?: string; recordedAt?: string }
type YearClosingVatInput = {
  year: number
  reports?: { trial?: Array<{ accounts?: Array<{ nr?: unknown; code?: unknown; name?: unknown; ut?: unknown }> }> }
  settings?: { vatRegistered?: unknown }
  chartAccounts?: Array<{ code?: unknown; accountNumber?: unknown; equivalentCode?: unknown; standardAccountCode?: unknown; yearClosingStandardAccountCode?: unknown }>
  scope?: { jurisdiction?: string; productSupport?: string }
  evidence?: YearClosingVatEvidence | null
  vatReturnsReady?: boolean
}

const yearClosingVatCleanupApi = (() => {
  const cents = (value: number) => Math.round(value * 100) / 100
  const baseCode = (value: unknown) => String(value || '').match(/^\d{4}/)?.[0] || ''
  const build = ({ year, reports, settings = {}, chartAccounts = [], scope, evidence, vatReturnsReady = true }: YearClosingVatInput) => {
    let invalidBalance = !Array.isArray(reports?.trial)
    const rows: YearClosingVatRow[] = (reports?.trial || []).flatMap((group) => group.accounts || []).flatMap((account) => {
      const code = String(account.nr || account.code || '')
      const chart = chartAccounts.find((item) => String(item.code || item.accountNumber || '') === code)
      const standardCode = baseCode(chart?.equivalentCode || chart?.standardAccountCode || chart?.yearClosingStandardAccountCode || code)
      if (!/^27[0-4]\d$/.test(standardCode)) return []
      const balance = Number(account.ut)
      if (!Number.isFinite(balance)) invalidBalance = true
      return [{ code, standardCode, name: String(account.name || ''), balance: cents(balance), kind: /^27[01]\d$/.test(standardCode) ? 'control' as const : standardCode === '2740' ? 'settlement' as const : 'other' as const }]
    }).sort((a, b) => a.code.localeCompare(b.code))
    const controls = rows.filter((row) => row.kind === 'control')
    const settlement = rows.filter((row) => row.kind === 'settlement')
    const openControls = controls.filter((row) => Math.abs(row.balance) > 0.005)
    const otherOpen = rows.filter((row) => row.kind === 'other' && Math.abs(row.balance) > 0.005)
    const settlementBalance = cents(settlement.reduce((sum, row) => sum + row.balance, 0))
    const settlementHasBalance = settlement.some((row) => Math.abs(row.balance) > 0.005)
    const hasEvidence = evidence?.year === year && typeof evidence.expectedBalance === 'number' && Number.isFinite(evidence.expectedBalance) && Boolean(evidence.documentationNote?.trim())
    const difference = hasEvidence ? cents(settlementBalance - Number(evidence?.expectedBalance)) : null
    const settlementReady = hasEvidence ? difference === 0 : !settlementHasBalance
    const supported = scope?.jurisdiction === 'NO' && ['supported', 'partial'].includes(scope?.productSupport || '')
    const noActivity = rows.every((row) => row.balance === 0)
    const reason = !supported ? 'unsupported' : invalidBalance ? 'unknown'
      : settings.vatRegistered === false && noActivity && !hasEvidence ? 'notApplicable'
      : settings.vatRegistered !== true && settings.vatRegistered !== false ? 'unknown'
      : !vatReturnsReady ? 'returnsOpen'
      : openControls.length || otherOpen.length ? 'controlsOpen'
      : !settlementReady ? hasEvidence ? 'difference' : 'settlementReview' : 'ready'
    const status = reason === 'ready' ? 'ok' : reason === 'notApplicable' ? 'na' : 'todo'
    const canTransfer = supported && vatReturnsReady && !invalidBalance
    const canReconcile = canTransfer && openControls.length === 0 && otherOpen.length === 0
    return {
      year, rows, controls, openControls, settlement, settlementBalance, otherOpen, evidence: evidence || null,
      difference, settlementReady, supported, vatReturnsReady, canTransfer, canReconcile, status, reason,
      count: status === 'todo' ? Math.max(1, openControls.length + otherOpen.length + (settlementReady ? 0 : 1)) : 0,
      // A single mapped settlement account is required for a deterministic prefill.
      settlementAccountCode: settlement.length === 1 ? settlement[0].code : settlement.length === 0 ? '2740' : '',
    }
  }
  const transferPrefill = (model: ReturnType<typeof build>, code: string) => {
    const account = model.openControls.find((row) => row.code === code)
    if (!model.canTransfer || model.reason === 'unknown' || !account || !model.settlementAccountCode || !Number.isFinite(account.balance)) return null
    return {
      fromAccountCode: account.balance > 0 ? account.code : model.settlementAccountCode,
      toAccountCode: account.balance > 0 ? model.settlementAccountCode : account.code,
      amount: Math.abs(account.balance), date: `${model.year}-12-31`,
    }
  }
  const reconciliationEvidence = (model: ReturnType<typeof build>, documentationNote: string) => {
    const note = String(documentationNote || '').trim()
    if (!model.canReconcile || !Number.isFinite(model.settlementBalance) || !note) return null
    return { expectedBalance: model.settlementBalance, documentationNote: note }
  }
  return { build, transferPrefill, reconciliationEvidence }
})()

window.PlummeYearClosingVatCleanup = yearClosingVatCleanupApi
